import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/useApi';
import { useListingPaths } from '../lib/listingPaths';
import { apiRequest, ApiError } from '../lib/api';
import { money, shortDate } from '../lib/format';
import {
  Alert, Button, ButtonLink, Card, EmptyState, ErrorBlock, Field, LoadingBlock,
  StatusPill, TextInput,
} from '../components/ui';

/**
 * Advertising allowance for homes you do not manage here (migration 133).
 *
 * A landlord's own vacancies are advertised free, because the subscription
 * already bills the unit. This page is for the other case — a home on somebody
 * else's books that an agent has been asked to let — and the one thing it must
 * never do is blur the two. Hence the first line on the page.
 *
 * Nothing here moves money. The buyer says they have paid and quotes the code;
 * a person at NyumbaSuite confirms it. Saying so where the button is, rather
 * than in a help page, is what stops somebody waiting for an advert to appear.
 */

interface Bundle {
  code: string; name: string; description: string | null;
  days: number; max_listings: number; price: string; currency_code: string;
  once_per_account: boolean;
}

interface Purchase {
  id: string; bundle_code: string; days: number; max_listings: number;
  amount: string; currency_code: string; payment_reference: string | null;
  state: string; requested_at: string; confirmed_at: string | null;
  covers_from: string | null; covers_until: string | null; reject_reason: string | null;
}

/** An advert for a home the account does not manage here. */
interface External {
  id: string; title: string; status: string; rent: string;
  county: string | null; estate_area: string | null;
  paid_until: string | null; on_site: boolean; needs_allowance: boolean;
  enquiries: number; photos: number;
}

interface Info {
  bundles: Bundle[];
  purchases: Purchase[];
  allowance: { allowed: number; used: number; remaining: number; until: string | null };
  pricingConfigured: boolean;
}

const TONE: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  pending: 'info', confirmed: 'success', rejected: 'danger', refunded: 'neutral',
};

export function ListingBundles() {
  const paths = useListingPaths();
  const { data, error, loading, reload } = useApi<Info>('/api/v1/listing-bundles');
  const [buying, setBuying] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const buy = async (code: string) => {
    setBusy(true); setProblem(null);
    try {
      await apiRequest('/api/v1/listing-bundles', {
        method: 'POST',
        body: { bundleCode: code, paymentReference: reference.trim() || undefined },
      });
      setBuying(null); setReference(''); setDone(true);
      reload();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : 'That did not go through.');
    } finally { setBusy(false); }
  };

  const withdraw = async (id: string) => {
    setBusy(true); setProblem(null);
    try {
      await apiRequest(`/api/v1/listing-bundles/${id}`, { method: 'DELETE' });
      reload();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : 'That did not go through.');
    } finally { setBusy(false); }
  };

  const a = data?.allowance;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Advertising allowance</h1>
          <p className="page-sub">
            For homes you do not manage on NyumbaSuite. Your own vacancies are
            advertised free, <Link to={paths.home}>list those from Vacancies</Link>.
          </p>
        </div>
      </div>

      {problem && <Alert tone="danger">{problem}</Alert>}
      {done && (
        <Alert tone="success">
          We have your payment details. Your allowance starts once somebody here
          confirms the money — nothing is advertised before then.
        </Alert>
      )}

      {loading ? <LoadingBlock />
        : error ? <Card><ErrorBlock error={error} onRetry={reload} /></Card>
          : (
            <>
              <Card title="What you can advertise now">
                <div className="grid grid-3">
                  <div>
                    <div className="muted">Places</div>
                    <div className="kpi-value">{a!.allowed}</div>
                  </div>
                  <div>
                    <div className="muted">In use</div>
                    <div className="kpi-value">{a!.used}</div>
                  </div>
                  <div>
                    <div className="muted">Free</div>
                    <div className="kpi-value">{a!.remaining}</div>
                  </div>
                </div>
                {a!.until && (
                  <p className="muted" style={{ marginTop: 8 }}>
                    Your cover runs to {shortDate(a!.until)}. Each advert ends with the
                    bundle it was placed on, so a short bundle running out takes down
                    only the advert drawing on it.
                  </p>
                )}
                {a!.allowed === 0 && (
                  <p className="muted" style={{ marginTop: 8 }}>
                    You have no allowance yet, so a home you do not manage here can be
                    prepared and reviewed but not put on the site.
                  </p>
                )}
              </Card>

              {!data!.pricingConfigured ? (
                <Card>
                  <EmptyState
                    title="Nothing on sale yet"
                    message="Advertising bundles have not been priced on this deployment. Nobody can buy allowance until they are."
                  />
                </Card>
              ) : (
                <Card title="Buy allowance">
                  <div className="grid grid-3">
                    {data!.bundles.map((b) => (
                      <div key={b.code} className="plan-card">
                        <h3>{b.name}</h3>
                        <p className="kpi-value">{money(b.price)}</p>
                        <p className="muted">
                          {b.max_listings} advert{b.max_listings === 1 ? '' : 's'} at a time
                          {' · '}{b.days} days
                        </p>
                        {b.description && <p>{b.description}</p>}
                        {b.once_per_account && (
                          <p className="muted">One per account.</p>
                        )}
                        {buying === b.code ? (
                          <>
                            <Field label="M-Pesa code" htmlFor={`ref-${b.code}`}
                              hint="From the message on your phone. Leave it blank if you do not have it yet.">
                              <TextInput id={`ref-${b.code}`} value={reference}
                                onChange={(e) => setReference(e.target.value)} />
                            </Field>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <Button size="sm" loading={busy} onClick={() => buy(b.code)}>
                                I have paid
                              </Button>
                              <Button size="sm" variant="secondary"
                                onClick={() => setBuying(null)}>Cancel</Button>
                            </div>
                          </>
                        ) : (
                          <Button size="sm" variant="secondary" block
                            onClick={() => { setBuying(b.code); setReference(''); setDone(false); }}>
                            Buy this
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/*
                * THE ADVERTS THEMSELVES.
                *
                * Vacancies is driven from units, so it can never show an advert
                * that has no unit behind it — which left an external advert
                * with no screen at all, not even to the person who wrote it,
                * and no way to spend the place they had just paid for. This is
                * that screen: it is where an external advert lives, is put up,
                * and is taken down.
                */}
              <Adverts allowance={a!} onChange={reload} />

              {data!.purchases.length > 0 && (
                <Card title="Your purchases" bodyClassName="card-pad-0">
                  <div className="table-wrap">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Bundle</th><th>Asked</th><th className="num">Amount</th>
                          <th>State</th><th>Covers</th><th />
                        </tr>
                      </thead>
                      <tbody>
                        {data!.purchases.map((p) => (
                          <tr key={p.id}>
                            <td>
                              {p.bundle_code}
                              <br />
                              <span className="muted">
                                {p.max_listings} at a time · {p.days} days
                              </span>
                            </td>
                            <td>{shortDate(p.requested_at)}</td>
                            <td className="num">
                              {money(p.amount)}
                              {Number(p.amount) === 0 && (
                                <>
                                  <br />
                                  <span className="muted">given, not sold</span>
                                </>
                              )}
                            </td>
                            <td>
                              <StatusPill status={p.state} tone={TONE[p.state] ?? 'neutral'} />
                              {p.reject_reason && (
                                <>
                                  <br />
                                  <span className="muted">“{p.reject_reason}”</span>
                                </>
                              )}
                            </td>
                            <td>
                              {p.covers_until
                                ? `to ${shortDate(p.covers_until)}`
                                : <span className="muted">not yet</span>}
                            </td>
                            <td>
                              {p.state === 'pending' && (
                                <Button size="sm" variant="secondary" loading={busy}
                                  onClick={() => withdraw(p.id)}>Withdraw</Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              )}
            </>
          )}
    </div>
  );
}

/**
 * The account's external adverts, and the two presses that matter.
 *
 * `on_site` is the honest answer to "is this on Find Your Next Home", and it is
 * NOT the same as the status: an external advert can read `published` and be
 * invisible, because the public read policy also asks for a live paid window.
 * The row says which, in words, because a landlord looking at "Published" while
 * nobody can find their home has no way to guess.
 */
function Adverts({ allowance, onChange }: {
  allowance: { allowed: number; used: number; remaining: number; until: string | null };
  onChange: () => void;
}) {
  const paths = useListingPaths();
  const { data, error, loading, reload } = useApi<{ listings: External[] }>(
    '/api/v1/listings?external=1');
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const rows = data?.listings ?? [];

  const act = async (id: string, method: 'POST' | 'DELETE') => {
    setBusy(id); setProblem(null);
    try {
      await apiRequest(`/api/v1/listings/${id}/slot`, { method });
      reload(); onChange();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : 'That did not go through.');
    } finally { setBusy(null); }
  };

  return (
    <Card title="Homes you do not manage here">
      {problem && <Alert tone="danger">{problem}</Alert>}

      {loading ? <LoadingBlock />
        : error ? <ErrorBlock error={error} onRetry={reload} />
          : rows.length === 0
            ? (
              <EmptyState
                title="Nothing advertised yet"
                message="Advertise a home on somebody else's books — a flat you are letting for an owner, or a property you manage off-platform. You prepare it free; a place from your allowance puts it on the site."
                action={<ButtonLink to={paths.newExternal}>Advertise a home</ButtonLink>}
              />
            )
            : (
              <>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Advert</th><th>Where</th><th className="num">Rent</th>
                        <th>On the site?</th><th className="num">Enquiries</th><th />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <Link to={paths.listing(r.id)}>{r.title}</Link>
                            <br />
                            <span className="muted">
                              {r.photos} photo{r.photos === 1 ? '' : 's'}
                            </span>
                          </td>
                          <td>{[r.estate_area, r.county].filter(Boolean).join(', ') || '—'}</td>
                          <td className="num">{money(r.rent)} a month</td>
                          <td>
                            {/* The status pill alone would say "published" for an
                                advert nobody can see. The sentence is the fact. */}
                            <StatusPill
                              status={r.on_site ? 'on the site' : r.status}
                              tone={r.on_site ? 'success' : r.needs_allowance ? 'warning' : 'neutral'}
                            />
                            <br />
                            <span className="muted">
                              {r.on_site
                                ? r.paid_until
                                  ? `Until ${shortDate(r.paid_until)}.`
                                  : 'Live.'
                                : r.needs_allowance
                                  ? 'Approved. Spend a place to put it on the site.'
                                  : r.status === 'draft'
                                    ? 'Not submitted yet.'
                                    : r.status === 'pending_review'
                                      ? 'With our team for review.'
                                      : r.status === 'rejected'
                                        ? 'Needs a change before it can go up.'
                                        : 'Not on the site.'}
                            </span>
                          </td>
                          <td className="num">{r.enquiries}</td>
                          <td>
                            {r.on_site ? (
                              <Button size="sm" variant="secondary" loading={busy === r.id}
                                onClick={() => act(r.id, 'DELETE')}>
                                Take it down
                              </Button>
                            ) : r.needs_allowance ? (
                              <Button size="sm" loading={busy === r.id}
                                disabled={allowance.remaining === 0}
                                onClick={() => act(r.id, 'POST')}>
                                Put it up
                              </Button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {allowance.remaining === 0 && rows.some((r) => r.needs_allowance) && (
                  <p className="muted" style={{ marginTop: 8 }}>
                    Every place you hold is in use. Take an advert down to free one, or buy
                    more allowance above.
                  </p>
                )}

                <p style={{ marginTop: 12 }}>
                  <ButtonLink to={paths.newExternal} variant="secondary">
                    Advertise another
                  </ButtonLink>
                </p>
              </>
            )}
    </Card>
  );
}
